import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Container,
  Grid,
  Paper,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PolicyIcon from '@mui/icons-material/Policy';
import LaunchIcon from '@mui/icons-material/Launch';
import { colors } from '../utils/colors';
import { trackViewCoverage } from '../utils/googleAnalytics';

function FeaturesSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleFeatureClick = (index) => {
    // Track coverage navigation
    trackViewCoverage(`feature_${index}`);

    if (index === 1) { // Case Law Search - second feature
      navigate('/coverage?expand=croatia-courts');
    } else if (index === 2) { // Regulatory Compliance - third feature
      navigate('/coverage?expand=croatia-regulators');
    } else {
      navigate('/coverage');
    }
  };

  const features = [
    {
      icon: <GavelIcon role="img" aria-label="Legislation Search Icon" sx={{ fontSize: 40, color: colors.darkRed }} />,
      title: t('features.legislation_search.title'),
      description: t('features.legislation_search.description'),
    },
    {
      icon: <SearchIcon role="img" aria-label="Case Law Search Icon" sx={{ fontSize: 40, color: colors.darkRed }} />,
      title: t('features.case_law_search.title'),
      description: t('features.case_law_search.description'),
    },
    {
      icon: <PolicyIcon role="img" aria-label="Regulatory Compliance Icon" sx={{ fontSize: 40, color: colors.darkRed }} />,
      title: t('features.regulatory_compliance.title'),
      description: t('features.regulatory_compliance.description'),
    },
    {
      icon: <UploadFileIcon role="img" aria-label="Document Analysis Icon" sx={{ fontSize: 40, color: colors.darkRed }} />,
      title: t('features.document_analysis.title'),
      description: t('features.document_analysis.description'),
    },
  ];

  return (
    <Box id="features" sx={{ py: 8, backgroundColor: colors.offWhite }}>
      <Container>
        <Typography
          variant="h2"
          align="center"
          sx={{
            mb: 6,
            color: colors.darkRed,
            fontFamily: 'Exo, sans-serif',
            fontWeight: 'bold'
          }}
        >
          {t('features.title')}
        </Typography>
        <Grid container spacing={4}>
          {features.map(({ icon, title, description }, index) => (
            <Grid item xs={12} sm={6} lg={3} key={index}>
              <Paper
                elevation={3}
                onClick={index !== 3 ? () => handleFeatureClick(index) : undefined}
                sx={{
                  p: 4,
                  height: '100%',
                  backgroundColor: 'white',
                  borderTop: `4px solid ${colors.red}`,
                  transition: 'all 0.3s ease',
                  cursor: index !== 3 ? 'pointer' : 'default',
                  position: 'relative',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  },
                }}
              >
                <Box sx={{ mb: 2, textAlign: 'center' }}>{icon}</Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', mb: 2, color: colors.red, textAlign: 'center' }}
                >
                  {title}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.darkRed, textAlign: 'center', mb: 3 }}>
                  {description}
                </Typography>
                {index !== 3 && (
                  <Box sx={{
                    position: 'absolute',
                    bottom: 16,
                    left: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: colors.red,
                    fontSize: '0.875rem',
                  }}>
                    <Typography variant="caption" sx={{ color: colors.red, fontWeight: 500 }}>
                      {t('features.view_coverage') || 'View Coverage'}
                    </Typography>
                    <LaunchIcon sx={{ fontSize: 16 }} />
                  </Box>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

export default FeaturesSection;
